import type { LayoutRecord } from "@/shared/domain/layouts/LayoutRecord";
import { MessagePathRecord, TopicRecord } from "@/shared/domain/topics";
import {
  SignalEntry,
  SignalPanelType,
} from "@/shared/domain/workspaces/signalValidation";
import { PanelType } from "@/shared/state/visualization";
import type {
  FilterStage,
  StageCombinator,
} from "@/shared/state/visualization/schema/v1/log-panel";

// Payload for opening a message path panel in the visualizer
export interface OpenMessagePathPayload {
  messagePath: MessagePathRecord;
  topic: TopicRecord;
  timestamp?: bigint;
}

// Payload for opening topic search in the visualizer
export interface OpenTopicSearchPayload {
  topic: TopicRecord;
  timestamp?: bigint;
}

// Payload for seeking the timer to a specific timestamp
export interface SeekTimerPayload {
  timestamp: bigint;
}

/**
 * Outcome of an `apply_layout` action. `applied: false` carries the reason the
 * workspace left its panels as they were: the layout was already active, a
 * later apply superseded this one while the data was loading, or the
 * workspace closed first.
 */
export type ApplyLayoutResult =
  | { applied: true }
  | { applied: false; reason: string };

/**
 * Payload for making a saved layout the open workspace's active layout, which
 * rebuilds its panels from the layout's rules.
 *
 * `onResult` fires exactly once, but unlike the panel-mutation results not
 * necessarily before `publish()` returns: a workspace still loading its data
 * holds the layout and applies it when the load finishes, because a layout
 * set mid-load is pinned as the starting layout instead of being hydrated.
 */
export interface ApplyLayoutActionPayload {
  layout: LayoutRecord;
  onResult: (result: ApplyLayoutResult) => void;
}

/** One signal to visualize: a message path together with its parent topic. */
export type WorkspaceActionEntry = SignalEntry;

/**
 * Where to place a newly created panel, relative to an existing panel.
 * A panel's layout item shares the panel's id, so `relativeToPanelId` is
 * resolved directly against the layout tree.
 */
export interface PanelPlacementInput {
  relativeToPanelId: string;
  orientation: "top" | "right" | "bottom" | "left";
}

/**
 * Handler-authored facts the caller should relay to the model;
 * `runPanelMutation` merges them into the tool's success payload. A closed
 * shape on purpose: the merge spreads it into an envelope that already holds
 * `success`, `action`, and `panel_id`, so an open record would let a handler
 * overwrite the envelope rather than add to it.
 */
export interface WorkspaceActionDetails {
  /**
   * Whether a Log panel's filters are live, or are waiting on the
   * user-confirmed index build that panels above the auto-build threshold
   * require. Panel state cannot see whether that build already ran, so
   * `pending_index_build` is a conservative "they may not be" rather than a
   * statement that it has not happened.
   */
  applied?: "applied" | "pending_index_build";
}

/**
 * Result reported back to the publisher of a panel-mutation workspace action.
 *
 * The PubSubBus invokes subscribers synchronously in the publisher's stack
 * (a documented contract on `AiAgentThreadPubSubBus.publish`), so `onResult`
 * fires before `publish()` returns. Handlers validate the whole batch and
 * only then dispatch, so a validation failure (`ok: false`, with or without
 * `invalidPaths` / `invalidTargets`) means no panel state was changed; an
 * unexpected-error failure says in its `error` text that the workspace may
 * have been left partially modified. `invalidPaths` carries
 * per-message-path reasons so an AI caller can adjust its request and retry.
 *
 * `panelType` reports the kind of panel the action landed on. Handlers that
 * create or extend a panel always set it, so an AI caller learns what its
 * signals are being rendered as instead of inferring it a turn later from the
 * visualizer state; handlers that only remove things leave it unset.
 *
 * `invalidTargets` is the same idea for an action that addresses something
 * other than a message path — a synthetic Log panel column, say — and so
 * cannot name its failures in a `message_path_id` field without lying about
 * what the value is.
 *
 * `details` is described on {@link WorkspaceActionDetails}.
 */
export type WorkspaceActionResult =
  | {
      ok: true;
      panelId: string;
      panelType?: PanelType;
      details?: WorkspaceActionDetails;
    }
  | {
      ok: false;
      error: string;
      invalidPaths?: { messagePathId: string; reason: string }[];
      invalidTargets?: { target: string; reason: string }[];
    };

// Payload for creating one panel holding one or more signals
export interface CreatePanelActionPayload {
  entries: WorkspaceActionEntry[];
  timestamp?: bigint;
  placement?: PanelPlacementInput;
  /**
   * Panel type to build, overriding the one the entries' data types imply.
   * See `validateSignalsForNewPanel`, which applies every other rule
   * unchanged.
   */
  panelType?: SignalPanelType;
  onResult: (result: WorkspaceActionResult) => void;
}

// Payload for adding signals to an existing panel
export interface AddToPanelActionPayload {
  panelId: string;
  entries: WorkspaceActionEntry[];
  onResult: (result: WorkspaceActionResult) => void;
}

// Payload for removing signals from an existing panel
export interface RemoveFromPanelActionPayload {
  panelId: string;
  messagePathIds: string[];
  onResult: (result: WorkspaceActionResult) => void;
}

// Payload for removing an entire panel
export interface RemovePanelActionPayload {
  panelId: string;
  onResult: (result: WorkspaceActionResult) => void;
}

/**
 * The Log panel columns that exist without a message path behind them:
 * "all_columns" (pan-column search, mutually exclusive with per-column
 * filters) and "log_time" (the timestamp column; values are nanosecond-epoch
 * strings, and it is exempt from the all-columns exclusivity rule).
 */
export const SYNTHETIC_FILTER_TARGETS = ["all_columns", "log_time"] as const;

export type SyntheticFilterTarget = (typeof SYNTHETIC_FILTER_TARGETS)[number];

/** One filter edit on one Log panel column. */
export interface LogPanelFilterEdit {
  /**
   * The column to filter: a message path id currently on the panel, or one of
   * the synthetic targets.
   */
  target: { messagePathId: string } | SyntheticFilterTarget;
  /**
   * Filter stages for the column. With mode "replace", an empty array clears
   * the column's filter.
   */
  stages: FilterStage[];
  /** How multiple stages combine. Defaults to the column's current combinator, or "or". */
  combinator?: StageCombinator;
  /** "replace" swaps the column's filter wholesale; "append" adds stages to it. */
  mode: "replace" | "append";
}

// Payload for setting column filters on an existing Log panel
export interface SetLogPanelFiltersActionPayload {
  panelId: string;
  edits: LogPanelFilterEdit[];
  onResult: (result: WorkspaceActionResult) => void;
}

/**
 * The subset of workspace actions that mutate panels and report a result
 * through a synchronous `onResult` callback. Publishers that need the
 * request/response round-trip must build one of these.
 */
export type PanelMutationActionPayload = Extract<
  WorkspaceActionPayload,
  {
    type:
      | "create_panel"
      | "add_to_panel"
      | "remove_from_panel"
      | "remove_panel"
      | "set_log_panel_filters";
  }
>;

// Union type for all workspace action payloads
export type WorkspaceActionPayload =
  | { type: "open_message_path"; payload: OpenMessagePathPayload }
  | { type: "open_topic_search"; payload: OpenTopicSearchPayload }
  | { type: "seek_timer"; payload: SeekTimerPayload }
  | { type: "apply_layout"; payload: ApplyLayoutActionPayload }
  | { type: "create_panel"; payload: CreatePanelActionPayload }
  | { type: "add_to_panel"; payload: AddToPanelActionPayload }
  | { type: "remove_from_panel"; payload: RemoveFromPanelActionPayload }
  | { type: "remove_panel"; payload: RemovePanelActionPayload }
  | {
      type: "set_log_panel_filters";
      payload: SetLogPanelFiltersActionPayload;
    };

// PubSub topic names
export const WORKSPACE_ACTION_TOPIC = "workspace:action";
